import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { FiExternalLink, FiSearch } from "react-icons/fi";
import Particle from "../Particle";
import useInViewOnce from "../../hooks/useInViewOnce";
import VideoGallery from "./VideoGallery";
import {
  LAB_OWNER,
  labProjects,
  ownerLabels,
} from "./labProjects";

const ProjectCard = ({ project }) => {
  return (
    <div className="policy-card lab-project-card">
      {project.image ? (
        <div className="lab-project-image" aria-hidden="true">
          <img src={project.image} alt="" loading="lazy" />
        </div>
      ) : (
        <div
          className="lab-project-image lab-project-image--placeholder"
          aria-hidden="true"
        />
      )}

      <div className="lab-project-body">
        <div className="lab-project-top">
          <h3 className="lab-project-title">{project.title}</h3>
          <span className="lab-project-owner">{ownerLabels[project.owner]}</span>
        </div>

        {project.description ? (
          <p className="lab-project-desc">{project.description}</p>
        ) : null}

        {Array.isArray(project.tags) && project.tags.length > 0 ? (
          <div className="lab-project-tags" aria-label="Project tags">
            {project.tags.slice(0, 4).map((tag) => (
              <span key={tag} className="lab-tag">
                {tag}
              </span>
            ))}
          </div>
        ) : null}

        {project.href ? (
          <div className="lab-project-cta">
            <a
              className="btn custom-yellow-btn lab-cta-btn"
              href={project.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {project.cta ?? "View"} <FiExternalLink className="lab-cta-icon" />
            </a>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const getInternCategory = (project) => {
  const tags = Array.isArray(project.tags)
    ? project.tags.map((tag) => String(tag).toLowerCase())
    : [];

  if (tags.includes("portfolio")) return "portfolio";
  if (tags.includes("3d web") || tags.includes("three.js")) return "3d";
  if (tags.includes("game")) return "game";
  return "app";
};

const INTERN_CATEGORY_LABELS = {
  all: "All Projects",
  app: "Apps & Utilities",
  portfolio: "Portfolios",
  game: "Games",
  "3d": "3D Web",
};

const LabPage = () => {
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [internCategory, setInternCategory] = useState("all");
  const [selectedInternId, setSelectedInternId] = useState(null);
  const [internSort, setInternSort] = useState("default");

  const { ref: heroRef, hasEntered: heroSeen } = useInViewOnce({
    threshold: 0.16,
    once: true,
  });
  const { ref: featuredRef, hasEntered: featuredSeen } = useInViewOnce({
    threshold: 0.16,
    once: true,
  });
  const { ref: projectsRef, hasEntered: projectsSeen } = useInViewOnce({
    threshold: 0.12,
    once: true,
  });
  const { ref: modelingRef, hasEntered: modelingSeen } = useInViewOnce({
    threshold: 0.12,
    once: true,
  });

  const normalizedQuery = query.trim().toLowerCase();

  const filteredProjects = useMemo(() => {
    return labProjects.filter((project) => {
      if (ownerFilter !== "all" && project.owner !== ownerFilter) return false;

      if (!normalizedQuery) return true;
      const haystack = `${project.title ?? ""} ${project.description ?? ""} ${(project.tags ?? []).join(" ")}`.toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [ownerFilter, normalizedQuery]);

  const choicestProjects = filteredProjects.filter(
    (project) => project.owner === LAB_OWNER.choicest,
  );
  const internProjects = filteredProjects.filter(
    (project) => project.owner === LAB_OWNER.interns,
  );

  const internCategoryProjects = useMemo(() => {
    if (internCategory === "all") return internProjects;
    return internProjects.filter(
      (project) => getInternCategory(project) === internCategory,
    );
  }, [internProjects, internCategory]);

  const sortedInternProjects = useMemo(() => {
    if (internSort === "default") return internCategoryProjects;

    return [...internCategoryProjects].sort((a, b) =>
      String(a.title || "").localeCompare(String(b.title || "")),
    );
  }, [internCategoryProjects, internSort]);

  const selectedInternProject = useMemo(() => {
    if (!sortedInternProjects.length) return null;
    return (
      sortedInternProjects.find((project) => project.id === selectedInternId) ||
      sortedInternProjects[0]
    );
  }, [sortedInternProjects, selectedInternId]);

  useEffect(() => {
    if (!sortedInternProjects.length) {
      setSelectedInternId(null);
      return;
    }

    setSelectedInternId((current) => {
      const stillVisible = sortedInternProjects.some(
        (project) => project.id === current,
      );
      return stillVisible ? current : sortedInternProjects[0].id;
    });
  }, [sortedInternProjects]);

  const selectedInternIndex = useMemo(() => {
    if (!selectedInternProject) return -1;
    return sortedInternProjects.findIndex((project) => project.id === selectedInternProject.id);
  }, [selectedInternProject, sortedInternProjects]);

  const moveInternSelection = (delta) => {
    if (!sortedInternProjects.length) return;
    const start = selectedInternIndex >= 0 ? selectedInternIndex : 0;
    const next = (start + delta + sortedInternProjects.length) % sortedInternProjects.length;
    setSelectedInternId(sortedInternProjects[next].id);
  };

  const handleInternListKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveInternSelection(1);
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      moveInternSelection(-1);
    }
  };

  return (
    <Container fluid className="about-section lab-page">
      <Particle />

      <Container className="lab-container">
        <header
          ref={heroRef}
          className={`lab-hero lab-reveal${heroSeen ? " is-seen" : ""}`}
        >
          <h1 className="lab-title">
            Project <strong className="purple">Lab</strong>
          </h1>
          <p className="lab-lead">
            Explore real work from the Choicest Fruit team and our interns—web apps,
            3D web experiences, and creative experiments.
          </p>

          <div className="policy-card lab-controls" aria-label="Project filters">
            <div className="lab-controls-row">
              <div className="lab-search">
                <FiSearch aria-hidden="true" className="lab-search-icon" />
                <input
                  className="lab-search-input"
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search projects (e.g., Favrhood, 3D, portfolio)"
                  aria-label="Search projects"
                />
              </div>

              <div
                className="lab-pills"
                role="tablist"
                aria-label="Filter by creator"
              >
                <button
                  type="button"
                  className={`lab-pill${ownerFilter === "all" ? " is-active" : ""}`}
                  onClick={() => setOwnerFilter("all")}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`lab-pill${ownerFilter === LAB_OWNER.choicest ? " is-active" : ""}`}
                  onClick={() => setOwnerFilter(LAB_OWNER.choicest)}
                >
                  Choicest Fruit
                </button>
                <button
                  type="button"
                  className={`lab-pill${ownerFilter === LAB_OWNER.interns ? " is-active" : ""}`}
                  onClick={() => setOwnerFilter(LAB_OWNER.interns)}
                >
                  Interns
                </button>
              </div>
            </div>

            <div className="lab-jump" aria-label="Jump to section">
              <a className="lab-jump-link" href="#featured">
                Overview
              </a>
              <a className="lab-jump-link" href="#choicest">
                Choicest Fruit
              </a>
              <a className="lab-jump-link" href="#interns">
                Intern Projects
              </a>
              <a className="lab-jump-link" href="#modeling">
                3D Modeling
              </a>
            </div>
          </div>
        </header>

        <section
          id="featured"
          ref={featuredRef}
          className={`lab-section lab-reveal${featuredSeen ? " is-seen" : ""}`}
          aria-label="Lab overview"
        >
          <h2 className="about-section-heading">Lab Overview</h2>

          <div className="policy-card lab-featured">
            <Row className="align-items-center g-4">
              <Col md={12}>
                <h3 className="lab-featured-title">How To Explore The Project Lab</h3>
                <p className="lab-featured-desc">
                  Browse verified projects from the Choicest Fruit team and intern cohorts in a structured way.
                  Use search, creator filters, and project categories to quickly find what you want to review.
                </p>

                <div className="lab-project-tags" aria-label="Lab overview highlights">
                  <span className="lab-tag">Search & Filter</span>
                  <span className="lab-tag">Project Preview</span>
                  <span className="lab-tag">Direct Links</span>
                  <span className="lab-tag">Intern Showcase</span>
                </div>

                <div className="lab-featured-actions">
                  <a className="btn custom-yellow-btn" href="#interns">
                    Browse Intern Projects
                  </a>
                </div>
              </Col>
            </Row>
          </div>
        </section>

        <section
          ref={projectsRef}
          className={`lab-section lab-reveal${projectsSeen ? " is-seen" : ""}`}
          aria-label="Project lists"
        >
          <Row className="g-4">
            <Col lg={12}>
              <div id="choicest" className="lab-subsection">
                <h2 className="about-section-heading">Choicest Fruit Projects</h2>
                <p className="lab-sublead">
                  Products and platforms created by the Choicest Fruit team.
                </p>

                {choicestProjects.length === 0 ? (
                  <div className="policy-card lab-empty">
                    No matching Choicest Fruit projects.
                  </div>
                ) : (
                  <div className="lab-grid" role="list">
                    {choicestProjects.map((project) => (
                      <div key={project.id} role="listitem">
                        <ProjectCard project={project} />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div id="interns" className="lab-subsection">
                <h2 className="about-section-heading">Intern Projects</h2>
                <p className="lab-sublead">
                  Select a project from the list to preview details and open it.
                </p>

                {internProjects.length === 0 ? (
                  <div className="policy-card lab-empty">No matching intern projects.</div>
                ) : (
                  <div className="policy-card lab-intern-browser" aria-label="Intern project selector">
                    <div className="lab-intern-toolbar">
                      <div className="lab-intern-category-pills" role="tablist" aria-label="Filter intern projects">
                        {Object.entries(INTERN_CATEGORY_LABELS).map(([value, label]) => (
                          <button
                            key={value}
                            type="button"
                            className={`lab-intern-category-pill${internCategory === value ? " is-active" : ""}`}
                            onClick={() => setInternCategory(value)}
                            role="tab"
                            aria-selected={internCategory === value}
                          >
                            {label}
                          </button>
                        ))}
                      </div>

                      <label className="lab-intern-sort" htmlFor="lab-intern-sort">
                        <span>Sort</span>
                        <select
                          id="lab-intern-sort"
                          value={internSort}
                          onChange={(event) => setInternSort(event.target.value)}
                          aria-label="Sort intern projects"
                        >
                          <option value="default">Featured Order</option>
                          <option value="alpha">Alphabetical</option>
                        </select>
                      </label>

                      <p className="lab-intern-count" aria-live="polite">
                        {sortedInternProjects.length} project{sortedInternProjects.length === 1 ? "" : "s"}
                      </p>
                    </div>

                    <div className="lab-intern-layout">
                      <div
                        className="lab-intern-list"
                        aria-label="Intern projects"
                        role="listbox"
                        tabIndex={0}
                        onKeyDown={handleInternListKeyDown}
                      >
                        {sortedInternProjects.map((project) => (
                          <button
                            key={project.id}
                            type="button"
                            className={`lab-intern-item${selectedInternProject?.id === project.id ? " is-active" : ""}`}
                            onClick={() => setSelectedInternId(project.id)}
                            role="option"
                            aria-selected={selectedInternProject?.id === project.id}
                          >
                            <span className="lab-intern-item-title">{project.title}</span>
                            <span className="lab-intern-item-meta">{project.description || "Intern project"}</span>
                          </button>
                        ))}
                      </div>

                      {selectedInternProject ? (
                        <article className="lab-intern-preview" aria-label="Selected intern project details">
                          {selectedInternProject.image ? (
                            <div className="lab-intern-preview-media" aria-hidden="true">
                              <img src={selectedInternProject.image} alt="" loading="lazy" />
                            </div>
                          ) : (
                            <div className="lab-intern-preview-media lab-project-image--placeholder" aria-hidden="true" />
                          )}

                          <div className="lab-intern-preview-body">
                            <p className="lab-intern-preview-kicker">Selected Project</p>
                            <h3 className="lab-intern-preview-title">{selectedInternProject.title}</h3>
                            <p className="lab-intern-preview-desc">
                              {selectedInternProject.description || "Project details available in the project link."}
                            </p>

                            {Array.isArray(selectedInternProject.tags) && selectedInternProject.tags.length > 0 ? (
                              <div className="lab-project-tags" aria-label="Selected project tags">
                                {selectedInternProject.tags.map((tag) => (
                                  <span key={tag} className="lab-tag">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            ) : null}

                            {selectedInternProject.href ? (
                              <div className="lab-featured-actions">
                                <a
                                  className="btn custom-yellow-btn"
                                  href={selectedInternProject.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {selectedInternProject.cta ?? "Open Project"} <FiExternalLink className="lab-cta-icon" />
                                </a>
                              </div>
                            ) : null}
                          </div>
                        </article>
                      ) : null}
                    </div>

                    {selectedInternProject ? (
                      <div className="lab-intern-mobile-nav" aria-label="Intern project quick navigation">
                        <button
                          type="button"
                          className="lab-intern-mobile-nav-btn"
                          onClick={() => moveInternSelection(-1)}
                        >
                          Previous
                        </button>
                        <p className="lab-intern-mobile-nav-title" aria-live="polite">
                          {selectedInternIndex + 1}/{sortedInternProjects.length} - {selectedInternProject.title}
                        </p>
                        <button
                          type="button"
                          className="lab-intern-mobile-nav-btn"
                          onClick={() => moveInternSelection(1)}
                        >
                          Next
                        </button>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </section>

        <section
          id="modeling"
          ref={modelingRef}
          className={`lab-section lab-reveal${modelingSeen ? " is-seen" : ""}`}
          aria-label="3D modeling and animation"
        >
          <h2 className="about-section-heading">3D Modeling & Animation</h2>
          <p className="lab-sublead">
            A gallery of modeling, simulation, and animation explorations.
          </p>

          <div className="policy-card lab-modeling-card">
            <VideoGallery />
          </div>
        </section>
      </Container>
    </Container>
  );
};

export default LabPage;
